import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  LikertHealthyResponse,
  LikertScaleBlock,
  LikertScaleOptionCount,
  LikertScaleTitleLevel,
  LikertScaleType,
} from '@course-authoring/shared-types';
import {
  getLikertScaleOptionLabels,
  LIKERT_OPTION_COUNTS,
  LIKERT_SCALE_OPTIONS,
  richTextToPlainText,
  useTheme,
} from '@course-authoring/shared-types';
import { useIsMobileViewport } from '@course-authoring/ui-blocks';

import { InteractionRichTextField } from './InteractionRichTextField';
import {
  createTokenRichTextToolbarStyles,
  EMPTY_RICH_TEXT_DOC,
} from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { QuestionSelector } from './questionBank/QuestionSelector';
import {
  ManagedInBankNote,
  createReadOnlyTextStyle,
  resolveQuestionEditorMode,
} from './questionBank/questionEditorShared';
import { FieldOverrideStatus } from './questionBank/FieldOverrideStatus';
import { useFieldOverrideState } from './questionBank/useFieldOverrideState';
import { useQuestionTextOverride } from './questionBank/useQuestionTextOverride';

export interface LikertScaleEditorProps {
  block: LikertScaleBlock;
  onChange: (updates: Partial<LikertScaleBlock>) => void;
  autoFocus?: boolean;
  /**
   * 'block' (default): lesson-block mode — title controls + QuestionSelector,
   * question content rendered read-only from the resolved block fields.
   * 'questionModal': create-question modal mode — editable question fields,
   * no title controls, no lock toggles.
   */
  variant?: 'block' | 'questionModal';
  translationMode?: boolean;
  /** NA-198: when true (question in use in a published/archived lesson), disallows decreasing the option count below the persisted floor. */
  disableOptionRemoval?: boolean;
  /** The persisted point count (5 or 7) at panel-open time — the floor decreasing can't cross while locked; increasing back up to it (or beyond) always stays allowed. */
  lockedMinOptionCount?: number;
}

type FieldName = 'title' | 'description' | 'scale' | 'optionCount' | 'healthyResponse';

const editorInnerStyles = `
  .likert-editor p {
    margin: 0;
  }

  .likert-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

export function LikertScaleEditor({
  block,
  onChange,
  autoFocus = false,
  variant = 'block',
  translationMode = false,
  disableOptionRemoval = false,
  lockedMinOptionCount,
}: LikertScaleEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const { isModal, isManaged, showBlockAuthoringControls } = resolveQuestionEditorMode(
    variant,
    block,
    translationMode,
  );

  const titleLevel = block.titleLevel ?? 'h2';
  const scale = block.scale ?? 'agreement';
  const optionCount = block.optionCount ?? 7;
  const healthyResponse = block.healthyResponse ?? 'none';
  const interactionWidth = isMobile
    ? '90%'
    : '80%';

  const handleFieldFocus = useCallback((field: FieldName) => {
    setFocusedField(field);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  // Still needed for the read-only translation-mode display below.
  const descriptionText = richTextToPlainText(block.description);

  // NA-382: question text is authored on the block (or on the bank value in the
  // modal); see useQuestionTextOverride for why the commit is on blur.
  const questionText = useQuestionTextOverride(block, onChange, isModal, translationMode);

  // NA-384: title, same override model, plain string instead of rich text.
  // See the WCS editor's identical comment for the full reasoning — ownership
  // is only claimed while the bank actually has a title, checked via
  // `titleBankValue` rather than `questionDefinitionId` since the former is
  // reliable immediately after a fresh selection.
  const titleOverride = useFieldOverrideState<LikertScaleBlock, string>(block, {
    value: 'title',
    marker: 'titleOverridden',
    bankValue: 'titleBankValue',
    requireBankValue: true,
  });
  const hasBankTitle = block.titleBankValue !== undefined;

  const handleTitleChange = useCallback(
    (next: string) => onChange(
      hasBankTitle && !translationMode ? titleOverride.buildEdit(next) : { title: next },
    ),
    [hasBankTitle, translationMode, titleOverride, onChange],
  );

  const handleTitleRevert = useCallback(
    () => onChange(titleOverride.buildRevert()),
    [titleOverride, onChange],
  );

  const richTextToolbarStyles = useMemo(
    () => createTokenRichTextToolbarStyles(tokens),
    [tokens]
  );

  const healthyResponseOptions = useMemo((): Array<{ value: LikertHealthyResponse; label: string }> => {
    const labels = getLikertScaleOptionLabels(scale, optionCount);
    const leftLabel = labels[0] ?? '';
    const rightLabel = labels[labels.length - 1] ?? '';
    return [
      { value: 'none', label: 'None' },
      { value: 'left', label: leftLabel },
      { value: 'right', label: rightLabel },
    ];
  }, [scale, optionCount]);

  const styles = useMemo(
    (): Record<string, CSSProperties> => ({
      container: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing.md,
        ...(isModal ? {} : { width: interactionWidth, margin: '0 auto' }),
        maxWidth: '100%',
        boxSizing: 'border-box',
      },
      header: {
        marginBottom: tokens.spacing.xs,
      },
      titleInput: {
        ...getTitleInputStyle(titleLevel, tokens),
        marginBottom: tokens.spacing.sm,
      },
      questionTextLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: tokens.text.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: tokens.spacing.xs,
      },
      readOnlyText: createReadOnlyTextStyle(tokens),
      descriptionEditor: {
        fontFamily: tokens.typography.fontFamily,
        fontSize: tokens.typography.body.size,
        color: tokens.text.muted,
        lineHeight: tokens.typography.body.lineHeight,
      },
      settingsCard: {
        backgroundColor: tokens.backgrounds.app,
        borderRadius: tokens.radius.md,
        padding: tokens.spacing.lg,
        border: `1px solid ${tokens.borders.default}`,
        boxSizing: 'border-box',
      },
      sectionTitleRow: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: tokens.spacing.sm,
        marginBottom: tokens.spacing.md,
      },
      sectionTitleText: {
        fontSize: '13px',
        fontWeight: 700,
        color: tokens.text.primary,
      },
      settingsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: tokens.spacing.md,
      },
      fieldWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing.xs,
      },
      fieldLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: tokens.text.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      selectInput: {
        width: '100%',
        border: `1px solid ${tokens.borders.default}`,
        borderRadius: tokens.radius.sm,
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        fontFamily: tokens.typography.fontFamily,
        fontSize: tokens.typography.body.size,
        fontWeight: 500,
        color: tokens.text.primary,
        outline: 'none',
        backgroundColor: tokens.backgrounds.app,
        boxSizing: 'border-box',
        appearance: 'none',
        cursor: isModal ? 'pointer' : 'default',
      },
      healthyResponseCard: {
        backgroundColor: tokens.backgrounds.app,
        borderRadius: tokens.radius.md,
        padding: tokens.spacing.lg,
        boxSizing: 'border-box',
      },
      healthyResponseLabelRow: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: tokens.spacing.sm,
        marginBottom: tokens.spacing.sm,
      },
      healthyResponseLabel: {
        fontSize: '12px',
        fontWeight: 700,
        color: tokens.text.primary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      healthyResponseGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacing.sm,
      },
      healthyResponsePill: {
        padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
        borderRadius: '999px',
        border: `1px solid ${tokens.borders.default}`,
        backgroundColor: tokens.backgrounds.app,
        color: tokens.text.primary,
        fontFamily: tokens.typography.fontFamily,
        fontSize: tokens.typography.body.size,
        fontWeight: 500,
        cursor: isModal ? 'pointer' : 'default',
        transition: 'background-color 150ms, color 150ms, border-color 150ms',
      },
      healthyResponsePillActive: {
        backgroundColor: tokens.brand.primary,
        color: tokens.text.onBrand,
        borderColor: tokens.brand.primary,
      },
    }),
    [interactionWidth, isModal, titleLevel, tokens],
  );

  return (
    <div ref={containerRef} style={styles.container}>
      <style>{editorInnerStyles}</style>

      {!isModal && (
        <div style={styles.header}>
          {showBlockAuthoringControls && (
            <TitleLevelSelector
              currentLevel={titleLevel}
              onSelectLevel={(level) => onChange({ titleLevel: level as LikertScaleTitleLevel })}
              tokens={tokens}
            />
          )}

          <input
            type="text"
            value={titleOverride.displayValue ?? ''}
            placeholder="Enter title..."
            onChange={(event) => handleTitleChange(event.target.value)}
            onFocus={() => handleFieldFocus('title')}
            onBlur={handleFieldBlur}
            style={styles.titleInput}
            autoFocus={autoFocus}
          />
          {!translationMode && (
            <FieldOverrideStatus
              isDiverged={titleOverride.isDiverged}
              onRevert={handleTitleRevert}
              fieldLabel="title"
            />
          )}

          {showBlockAuthoringControls && (
            <QuestionSelector
              block={block}
              onChange={onChange as (updates: Record<string, unknown>) => void}
            />
          )}
        </div>
      )}

      {(isModal || isManaged) && (
        <>
      {/*
        Question text.

        NA-382: block-overridable, so it is editable in BOTH the create-question
        modal (authoring the bank value) and the lesson block (overriding it) —
        hence no `isModal` gate and no managed note on this card. Scale settings
        and healthy response stay read-only and managed. It was previously
        read-only PLAIN TEXT in block mode, so rich formatting becomes visible
        here for the first time.

        The label row keeps Likert's own card-title shape rather than switching to
        `QuestionSectionLabel`, so this card still matches "Scale settings" beside
        it; that row already carries an inline trailing note, so the status sits
        where the managed note used to.
      */}
      {questionText.isEditable ? (
        <div style={isModal ? undefined : styles.settingsCard}>
          {isModal ? (
            <div style={styles.questionTextLabel}>Question text</div>
          ) : (
            <div style={styles.sectionTitleRow}>
              <span style={styles.sectionTitleText}>Question text</span>
              {questionText.showStatus && (
                <FieldOverrideStatus
                  isDiverged={questionText.override.isDiverged}
                  onRevert={questionText.onRevert}
                  fieldLabel="question text"
                />
              )}
            </div>
          )}
          <InteractionRichTextField
            content={questionText.content ?? EMPTY_RICH_TEXT_DOC}
            placeholder="Enter question text..."
            isFocused={focusedField === 'description'}
            onFocus={() => handleFieldFocus('description')}
            onBlur={handleFieldBlur}
            onContentChange={questionText.onContentChange}
            autoFocus={autoFocus}
            containerRef={containerRef}
            editorClassName="tiptap likert-editor"
            editorStyle={styles.descriptionEditor}
            toolbarPreset="full"
            toolbarStyles={richTextToolbarStyles}
          />
        </div>
      ) : descriptionText !== '' ? (
        /*
          Translation mode, question text NOT overridden: the field stays Question
          Bank-owned, so it is shown as read-only localized context rather than as
          an editable block field (`lesson-block-translation`). Editing it here
          would let a translation claim ownership, which the default-locale scope
          alone may do.
        */
        <div style={styles.settingsCard}>
          <div style={styles.sectionTitleRow}>
            <span style={styles.sectionTitleText}>Question text</span>
            <ManagedInBankNote when={isManaged} />
          </div>
          <div style={styles.readOnlyText}>{descriptionText}</div>
        </div>
      ) : null}

      <div style={styles.settingsCard}>
        <div style={styles.sectionTitleRow}>
          <span style={styles.sectionTitleText}>Scale settings</span>
          <ManagedInBankNote when={isManaged} />
        </div>

        <div style={styles.settingsGrid}>
          <div style={styles.fieldWrapper}>
            <div style={styles.fieldLabel}>Scale</div>
            <select
              aria-label="Likert scale"
              value={scale}
              onChange={(event) => onChange({ scale: event.target.value as LikertScaleType })}
              onFocus={() => handleFieldFocus('scale')}
              onBlur={handleFieldBlur}
              style={styles.selectInput}
              disabled={!isModal}
            >
              {LIKERT_SCALE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.fieldWrapper}>
            <div style={styles.fieldLabel}>Number of options</div>
            <select
              aria-label="Likert option count"
              value={optionCount}
              onChange={(event) =>
                onChange({
                  optionCount: Number(event.target.value) as LikertScaleOptionCount,
                })
              }
              onFocus={() => handleFieldFocus('optionCount')}
              onBlur={handleFieldBlur}
              style={styles.selectInput}
              disabled={!isModal}
              title={disableOptionRemoval ? "Can't reduce options below the persisted count for a question used in a published lesson" : undefined}
            >
              {LIKERT_OPTION_COUNTS.map((count) => (
                <option
                  key={count}
                  value={count}
                  disabled={disableOptionRemoval && count < (lockedMinOptionCount ?? optionCount)}
                >
                  {count}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={styles.healthyResponseCard}>
        <div style={styles.healthyResponseLabelRow}>
          <span style={styles.healthyResponseLabel}>Healthy response</span>
          <ManagedInBankNote when={isManaged} />
        </div>
        <div style={styles.healthyResponseGroup} role="group" aria-label="Healthy response">
          {healthyResponseOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange({ healthyResponse: option.value })}
              onFocus={() => handleFieldFocus('healthyResponse')}
              onBlur={handleFieldBlur}
              style={{
                ...styles.healthyResponsePill,
                ...(healthyResponse === option.value ? styles.healthyResponsePillActive : {}),
              }}
              aria-pressed={healthyResponse === option.value}
              disabled={!isModal}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
        </>
      )}
    </div>
  );
}

LikertScaleEditor.displayName = 'LikertScaleEditor';
